import React from "react";
import { Crown, Shield, Handshake, Target } from "lucide-react";
import { Button } from "../ui/button";

export const LocalizedHeroSection = ({ t }) => {
  // Default English text as fallback
  const hero = t?.hero || {
    badge: "WORLD'S FIRST VIP CASINO NEGOTIATORS",
    title1: "We Negotiate With Casinos",
    titleHighlight: "So You Don't Have To",
    subtitle: "Tell us the cashback rate you want, the VIP perks you deserve, and we'll negotiate directly with top crypto casinos to make it happen. No middleman fees. Just better deals.",
    exploreCasinos: "Get My Custom Deal",
    readGuides: "How It Works",
    trustBadges: {
      partnerships: "Direct Casino Partnerships",
      vipDeals: "Personalized VIP Deals",
      freeService: "100% Free Service"
    },
  };

  return (
    <section
      data-testid="hero-section"
      className="relative flex items-center justify-center overflow-hidden pt-20 pb-4"
      aria-label="Hero section"
    >
      {/* Background Base */}
      <div className="absolute inset-0 z-0 bg-[#050505]" aria-hidden="true" />

      {/* Optimized Static Gradient Background - No animations for better LCP */}
      <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
        {/* Purple Gradient - Top Left */}
        <div 
          className="absolute w-[500px] h-[500px] rounded-full blur-[100px] opacity-60"
          style={{
            background: 'radial-gradient(circle, #A855F7 0%, transparent 70%)',
            top: '-5%',
            left: '-5%',
            contain: 'strict',
          }}
        />
        
        {/* Gold Gradient - Center Right */}
        <div 
          className="absolute w-[400px] h-[400px] rounded-full blur-[80px] opacity-50"
          style={{
            background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)',
            top: '25%',
            right: '-5%',
            contain: 'strict',
          }}
        />
        
        {/* Purple Gradient - Bottom */}
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-[120px] opacity-40"
          style={{
            background: 'radial-gradient(circle, #9333EA 0%, transparent 70%)',
            bottom: '-15%',
            left: '25%',
            contain: 'strict',
          }}
        />
      </div>

      {/* Subtle Noise Overlay for Texture */}
      <div className="absolute inset-0 z-[1] opacity-[0.02] pointer-events-none" aria-hidden="true" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-4 md:py-6">
        <div className="text-center max-w-4xl mx-auto">
          {/* World's First Badge - CSS animation instead of framer-motion for FCP */}
          <div className="inline-flex items-center gap-3 mb-8 animate-fade-up">
            <div className="flex items-center gap-2 glass-strong rounded-full px-5 py-2.5 border border-[#D4AF37]/20">
              <Handshake className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
              <span className="text-sm text-[#D4AF37] font-semibold tracking-wide">
                {hero.badge}
              </span>
            </div>
          </div>

          {/* Main Heading - Optimized for LCP */}
          <h1 className="font-['Unbounded'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.1] mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <span className="text-white">{hero.title1}</span>
            <br />
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #D4AF37 0%, #F59E0B 50%, #D4AF37 100%)" }}>
              {hero.titleHighlight}
            </span>
          </h1>

          {/* Subheading - Clear Value Prop */}
          <p className="text-lg md:text-xl text-[#A1A1AA] leading-relaxed max-w-3xl mx-auto mb-10 animate-fade-up" style={{ animationDelay: '0.2s' }}>
            {hero.subtitle}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12 animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <a href="#how-it-works" aria-label="Get My Custom Deal - Learn how it works">
              <Button
                data-testid="hero-join-btn"
                className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] hover:from-[#F59E0B] hover:to-[#D4AF37] text-black rounded-full px-10 py-6 text-sm font-bold shadow-lg shadow-[#D4AF37]/20 transition-all duration-300 min-h-[48px] min-w-[48px]"
              >
                <Target className="w-4 h-4 mr-2" aria-hidden="true" />
                {hero.exploreCasinos}
              </Button>
            </a>
            <a href="#vip-benefits" aria-label="How It Works - View VIP benefits">
              <Button
                data-testid="hero-guides-btn"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-full px-10 py-6 text-sm font-semibold transition-all duration-300 min-h-[48px] min-w-[48px]"
              >
                {hero.readGuides}
              </Button>
            </a>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-4 animate-fade-up" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#22C55E]/10 border border-[#22C55E]/20 min-h-[44px]">
              <div className="w-6 h-6 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                <Handshake className="w-3 h-3 text-[#22C55E]" aria-hidden="true" />
              </div>
              <span className="text-sm text-[#22C55E] font-medium">{hero.trustBadges.partnerships}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 min-h-[44px]">
              <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <Crown className="w-3 h-3 text-[#D4AF37]" aria-hidden="true" />
              </div>
              <span className="text-sm text-[#D4AF37] font-medium">{hero.trustBadges.vipDeals}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#A855F7]/10 border border-[#A855F7]/20 min-h-[44px]">
              <div className="w-6 h-6 rounded-full bg-[#A855F7]/20 flex items-center justify-center">
                <Shield className="w-3 h-3 text-[#A855F7]" aria-hidden="true" />
              </div>
              <span className="text-sm text-[#A855F7] font-medium">{hero.trustBadges.freeService}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator removed */}
    </section>
  );
};

export default LocalizedHeroSection;
